import React, { useState, useEffect } from "react";
import { Box, Flex, Heading, Text, useToast } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import useThemeColors from "../../../../../hooks/useThemeColors";
// import FormControlDefault from "../../../../../components/input/FormControlDefault";
import { textSecondary } from "../../../../../utils/colors";
import FormControlCountry from "../../../../../components/input/FormControlCountry";
// import FormControlAddressFull from "../../../../../components/input/FormControlAddressFull";
// import FormControlPhone from "../../../../../components/input/FormControlPhone";
import SaveButtons from "./SaveButtons";
import { useDispatch, useSelector } from "react-redux";
import { TENANT_UPDATE_REGIONAL_SETTINGS_RESET } from "../../../../../constants/tenantConstants";
import { updateTenantRegionalSettingsAction } from "../../../../../actions/tenantActions";
import FormControlCurrency from "../../../../../components/input/FormControlCurrency";
// import { useParams } from "react-router-dom";

const ClinicRegionalSettings = ({ slug, tenant }) => {
  // GENERAL
  // const { slug } = useParams();
  const { t } = useTranslation();
  const toast = useToast();

  // COLORS
  const { backgroundLightMode, newBorderMode } = useThemeColors();

  // STATE
  const [showButtons, setShowButtons] = useState(false);

  const [operatingCountry, setOperatingCountry] = useState(
    tenant?.operatingCountry || ""
  );
  const [currency, setCurrency] = useState(tenant?.currency || "");

  // REDUX
  const dispatch = useDispatch();

  const tenantUpdateRegionalSettings = useSelector(
    (state) => state.tenantUpdateRegionalSettings
  );
  const {
    loading: loadingUpdateClinicRegionalSettings,
    error: errorUpdateClinicRegionalSettings,
    success: successUpdateClinicRegional,
  } = tenantUpdateRegionalSettings;

  // HANDLERS
  const updateClinicRegionalSettingsHandler = () => {
    // console.log("Clinic regional settings to be sent:", {
    //   operatingCountry,
    // }); // ✅ Debugging log

    dispatch(
      updateTenantRegionalSettingsAction(
        slug,
        tenant.tenantID,
        operatingCountry,
        currency.value
      )
    );
  };

  // USE EFFECT

  const normalizeValue = (value) =>
    value === undefined || value === null ? "" : String(value).trim();

  useEffect(() => {
    const countryChanged =
      normalizeValue(operatingCountry) !==
      normalizeValue(tenant?.operatingCountry);
    const currencyChanged =
      normalizeValue(currency) !== normalizeValue(tenant?.currency);

    if (countryChanged || currencyChanged) {
      setShowButtons(true);
    } else {
      setShowButtons(false);
    }
  }, [operatingCountry, currency, tenant]);

  useEffect(() => {
    if (successUpdateClinicRegional) {
      toast({
        title: t("toasts.clinicRegionalSettingsUpdate"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      setShowButtons(false);
    } else if (errorUpdateClinicRegionalSettings) {
      toast({
        title: t("toasts.clinicRegionalSettingsUpdateError"),
        status: "error",
        duration: 3000,

        isClosable: true,
      });
    }

    dispatch({ type: TENANT_UPDATE_REGIONAL_SETTINGS_RESET });
  }, [successUpdateClinicRegional, errorUpdateClinicRegionalSettings]);

  // LOGS
  // console.log("tenant", tenant);
  // console.log("showButtons", showButtons);
  // console.log("clinicName", clinicName);
  // console.log("registrationEmail", registrationEmail);
  // console.log("supportEmail", supportEmail);
  // console.log("phone", phone);
  // console.log("address", address);
  // console.log("operatingCountry", operatingCountry);
  // console.log("currency", currency);

  return (
    <Box
      w="100%"
      p={["1.5rem 0", "3rem 0rem 3rem"]}
      borderBottom={`1px solid ${newBorderMode}`}
    >
      <Flex
        w="100%"
        justify="space-between"
        gap={["1.5rem", "2rem"]}
        direction={["column", "row"]}
      >
        {/* LEFT START */}
        <Flex direction="column" gap={2} w={["100%", "40%"]}>
          <Heading fontSize="1rem" fontWeight="600" p={["0 1.5rem", "initial"]}>
            {t("clinicSettings.clinicRegionalSettings")}
          </Heading>
          <Text
            color={textSecondary}
            fontSize="sm"
            fontWeight="500"
            p={["0 1.5rem", "initial"]}
          >
            {t("clinicSettings.clinicRegionalSettingsText")}
          </Text>
        </Flex>
        {/* LEFT END */}

        {/* RIGHT START */}
        <Flex
          w={["100%", "800px"]}
          direction="column"
          justify="flex-start"
          align="flex-start"
        >
          <Flex
            w="100%"
            bg={backgroundLightMode}
            borderRadius="1rem"
            p="1.25rem 1.5rem 1.25rem"
            direction="column"
          >
            <Flex w="100%" gap={["1rem", "2rem"]} direction={["column", "row"]}>
              <FormControlCountry
                label={t("form.operatingCountry")}
                size="lg"
                labelFontSize="0.8rem"
                value={operatingCountry}
                isRequired={true}
                onChange={(selectedOption) => {
                  setOperatingCountry(selectedOption.value); // Ensure it's a string
                }}
                w={["100%", "50%", "50%"]}
                errorMessage={t("formError.countryRequired")}
                isLabelEnabled
              />

              <FormControlCurrency
                label={t("form.currency")}
                value={currency}
                isRequired={true}
                labelFontSize="0.8rem"
                onChange={(val) => setCurrency(val)}
                // isTouched={formTouched}
                isError={!currency}
                errorMessage="Required"
                w={["100%", "50%", "50%"]}
              />
            </Flex>
          </Flex>
        </Flex>
        {/* RIGHT END */}
      </Flex>

      {/* BUTTONS */}
      {showButtons && (
        <SaveButtons
          onClose={() => {
            setShowButtons(false);
            setOperatingCountry(tenant?.operatingCountry || "");
            setCurrency(tenant?.currency || "");
          }}
          onClick={updateClinicRegionalSettingsHandler}
          loading
        />
      )}
    </Box>
  );
};

export default ClinicRegionalSettings;
